import { Wand2 } from "lucide-react";
import React from "react";

import type { CharOption } from "@/api/order_check";
import { Button } from "@/components/ui/button";
import {
  CharacteristicChoice,
  NO_CHARACTERISTIC,
} from "@/lib/scan-ui/characteristic-choice";
import { BottomSheet } from "@/lib/scan-ui/overlays";

/**
 * «Характеристику подставил портал» — и как это исправить в один-два тапа.
 *
 * Правило склада («что не предлагать при скане») может оставить у кода ровно
 * один вариант — тогда вопроса не будет вовсе, и характеристика в документ 1С
 * уедет та, которую выбрал ПОРТАЛ. Молча так делать нельзя: главный случай
 * ошибки — «сканировали ОПТ, а подставилось без характеристики», и чинить его
 * походом в настройку (она касается СЛЕДУЮЩИХ сканов) уже поздно.
 *
 * ⚠️ В выборе — ВСЕ варианты кода: и предложенные, и отброшенные правилом
 * (`hidden_characteristics`), и «Без характеристики». Правило — подсказка, а
 * не запрет: зафиксировать найденное на складе портал обязан всегда.
 *
 * Компонент чистый: открытием шторки владеет экран смены (от него зависят
 * перехват сканера и аппаратная «назад»), запрос делает он же.
 */
export function CharacteristicFix({
  name,
  options,
  open,
  busy,
  onOpen,
  onClose,
  onPick,
}: {
  /** Что подставлено; пусто — «без характеристики». */
  name: string;
  options: CharOption[];
  open: boolean;
  busy: boolean;
  onOpen: () => void;
  onClose: () => void;
  /** ⚠️ Отдаём ВЫБРАННЫЙ ВАРИАНТ: ссылку `none` сервер разворачивает сам. */
  onPick: (option: CharOption) => void;
}): React.ReactElement {
  const shown = name || "без характеристики";
  const answer = name
    ? {
        charRef: options.find((o) => o.name === name)?.ref ?? "",
        charName: name,
        withoutChar: false,
      }
    : { charRef: "", charName: "", withoutChar: true };
  return (
    <>
      <div className="mt-2 flex items-center gap-2 rounded-lg bg-amber-50 p-2 text-xs text-amber-900">
        <Wand2 aria-hidden className="size-4 shrink-0" />
        <span className="min-w-0 flex-1">
          характеристика подставлена правилом склада: <b>{shown}</b>
        </span>
        <Button size="sm" variant="outline" className="shrink-0" onClick={onOpen}>
          изменить
        </Button>
      </div>

      {open && (
        <BottomSheet onClose={onClose}>
          <div className="max-h-[70dvh] overflow-y-auto p-4">
            <h3 className="text-base font-semibold">Какая характеристика на самом деле?</h3>
            <p className="mt-1 text-xs text-slate-500">
              Портал подставил «{shown}»: остальные варианты этого кода выключены
              правилом склада. Выбранное уедет в документ 1С; количество и автор
              скана не изменятся.
            </p>
            <div className="mt-3">
              <CharacteristicChoice
                options={options}
                answer={answer}
                onPick={onPick}
                busy={busy}
              />
            </div>
          </div>
        </BottomSheet>
      )}
    </>
  );
}

/**
 * Все варианты характеристики этого скана: предложенные + отброшенные
 * правилом + «Без характеристики».
 *
 * ⚠️ «Без характеристики» добавляем сами, если сервер его не прислал: правило
 * могло отбросить все именованные варианты, и вернуть скан к «никакой» было
 * бы нечем — а это ровно тот случай, ради которого кнопка «изменить» и
 * появилась.
 */
export function allCharacteristics(res: {
  available_characteristics: CharOption[];
  hidden_characteristics: CharOption[];
}): CharOption[] {
  const byRef = new Map<string, CharOption>();
  for (const c of [...res.available_characteristics, ...res.hidden_characteristics]) {
    if (!byRef.has(c.ref)) byRef.set(c.ref, c);
  }
  if (!byRef.has(NO_CHARACTERISTIC)) {
    byRef.set(NO_CHARACTERISTIC, { ref: NO_CHARACTERISTIC, name: "" });
  }
  return [...byRef.values()];
}
